import { useEffect, useRef, useState } from 'react';
import { useGameStore } from '../../../store/gameStore';
import {
  playReactionPrompt,
  playReactionSuccess,
  playReactionMiss,
  playReactionDone,
} from '../../../utils/sounds';

type GamePhase = 'waiting' | 'prompt' | 'success' | 'miss' | 'done';

const TOTAL_REACTIONS = 10;
const PROMPT_WINDOW_MS = 500;

interface Props {
  active: boolean;
}

export function ReactionGame({ active }: Props) {
  const winGame = useGameStore((s) => s.winGame);
  const addScore = useGameStore((s) => s.addScore);

  const [gamePhase, setGamePhase] = useState<GamePhase>('waiting');
  const [reactions, setReactions] = useState(0);
  const [lastScore, setLastScore] = useState<number | null>(null);
  const [missCount, setMissCount] = useState(0);

  const phaseRef = useRef<GamePhase>('waiting');
  const promptStartRef = useRef(0);
  const reactionsRef = useRef(0);
  const ascendedRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  function setPhase(p: GamePhase) {
    phaseRef.current = p;
    setGamePhase(p);
  }

  function scheduleNextPrompt() {
    const delay = 1000 + Math.random() * 2000;
    timerRef.current = setTimeout(() => {
      if (phaseRef.current !== 'waiting') return;
      promptStartRef.current = performance.now();
      setPhase('prompt');
      playReactionPrompt();

      // Auto-miss if no response in window
      timerRef.current = setTimeout(() => {
        if (phaseRef.current !== 'prompt') return;
        setPhase('miss');
        playReactionMiss();
        setMissCount((m) => m + 1);
        timerRef.current = setTimeout(() => {
          setPhase('waiting');
          scheduleNextPrompt();
        }, 800);
      }, PROMPT_WINDOW_MS);
    }, delay);
  }

  useEffect(() => {
    if (!active) return;
    scheduleNextPrompt();
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  useEffect(() => {
    if (!active) return;

    function handleKey(e: KeyboardEvent) {
      if (e.code !== 'Space') return;
      if (phaseRef.current !== 'prompt') return;

      if (timerRef.current) clearTimeout(timerRef.current);

      const elapsed = performance.now() - promptStartRef.current;
      const score = Math.max(0, Math.floor((PROMPT_WINDOW_MS - elapsed) / 5));
      addScore(score);
      setLastScore(score);

      reactionsRef.current += 1;
      setReactions(reactionsRef.current);

      if (reactionsRef.current >= TOTAL_REACTIONS) {
        setPhase('done');
        playReactionDone();
        if (!ascendedRef.current) {
          ascendedRef.current = true;
          setTimeout(() => winGame(), 1200);
        }
      } else {
        setPhase('success');
        playReactionSuccess();
        timerRef.current = setTimeout(() => {
          setPhase('waiting');
          scheduleNextPrompt();
        }, 700);
      }
    }

    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, addScore, winGame]);

  const ringColors: Record<GamePhase, string> = {
    waiting: 'border-gray-600',
    prompt: 'border-yellow-400 animate-pulse',
    success: 'border-green-400',
    miss: 'border-red-500',
    done: 'border-purple-500',
  };

  const messages: Record<GamePhase, string> = {
    waiting: '...',
    prompt: 'PRESS SPACE!',
    success: lastScore !== null ? `+${lastScore}` : 'Nice!',
    miss: 'Too slow!',
    done: 'YOU WIN!',
  };

  const textColors: Record<GamePhase, string> = {
    waiting: 'text-gray-500',
    prompt: 'text-yellow-300 animate-pulse',
    success: 'text-green-400',
    miss: 'text-red-400',
    done: 'text-purple-400',
  };

  return (
    <div className="flex flex-col items-center justify-center gap-8 p-8 min-h-64">
      <div className="text-center">
        <div className="text-gray-400 text-sm font-mono mb-1">LAYER 3 — REACTION TEST</div>
        <div className="text-gray-300 font-mono">
          Reactions: <span className="text-white font-bold">{reactions}</span> / {TOTAL_REACTIONS}
        </div>
        {missCount > 0 && (
          <div className="text-red-500 text-xs mt-1">Misses: {missCount}</div>
        )}
      </div>

      <div
        className={`w-48 h-48 rounded-full border-8 flex items-center justify-center transition-colors duration-150 ${ringColors[gamePhase]}`}
      >
        <div className={`text-2xl font-bold text-center px-4 ${textColors[gamePhase]}`}>
          {messages[gamePhase]}
        </div>
      </div>

      {lastScore !== null && gamePhase === 'success' && (
        <div className="text-yellow-400 font-mono text-lg animate-pulse">+{lastScore} points</div>
      )}

      <div className="text-xs text-gray-600 font-mono text-center">
        Press <span className="text-gray-400">Space</span> when the ring flashes
        <br />
        Complete {TOTAL_REACTIONS} reactions to beat the game
      </div>
    </div>
  );
}
